import Table, { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import { styledTable } from '../styled/mixin';

import { ProjectRevenue, projectStatusKorean } from '../../modules/projects';
import { priceToString } from '../../util/utils';
import { Button, Col, DatePicker, Form, Input, Row, Select, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const StyledDiv = styled.div`
  ${styledTable}
`;

const { Option } = Select;

interface RevenueListComponentProps {
  onSearch: (values: any) => void;
  data: any[];
  moveToDetailPage: (data: any) => void;
}

function RevenueListComponent({
  onSearch,
  data,
  moveToDetailPage,
}: RevenueListComponentProps) {
  const [form] = Form.useForm();
  const dateFormat = 'YYYY-MM-DD';

  const columns: ColumnsType<ProjectRevenue> = [
    {
      title: '공사명',
      dataIndex: 'name',
      ellipsis: true,
      width: '15%',
      sorter: (a: ProjectRevenue, b: ProjectRevenue) =>
        a.name.localeCompare(b.name),
      render: (text: any, record: any) => (
        <>
          <span
            className="clickable-table-item"
            onClick={() => moveToDetailPage(record)}
          >
            {text}
          </span>
        </>
      ),
    },
    {
      title: '공사 시작일',
      dataIndex: 'startDate',
      width: '5%',
      sorter: (a: ProjectRevenue, b: ProjectRevenue) =>
        a.startDate.localeCompare(b.startDate),
    },
    {
      title: '공사 종료일',
      dataIndex: 'endDate',
      width: '5%',
      sorter: (a: ProjectRevenue, b: ProjectRevenue) =>
        a.endDate.localeCompare(b.endDate),
    },
    {
      title: '종류',
      dataIndex: 'type',
      align: 'center',
      sorter: (a: ProjectRevenue, b: ProjectRevenue) =>
        a.type?.localeCompare(b?.type),
      ellipsis: true,
      width: '5%',
    },
    {
      title: '발주처',
      dataIndex: 'owner',
      sorter: (a: ProjectRevenue, b: ProjectRevenue) =>
        a.owner?.localeCompare(b?.owner),
      ellipsis: true,
      width: '7%',
    },
    {
      title: '총계약금액(원)',
      width: '10%',
      align: 'right',
      render: (text: any, record: ProjectRevenue) => {
        const totalContractPrice = record.contracts.reduce(
          (acc: number, contract: any) => {
            if (!contract.priceData) return acc;
            return acc + contract.priceData.priceData.totalPrice;
          },
          0,
        );

        const totalChangeOrderPrice = record.changeOrders.reduce(
          (acc: number, changeOrder: any) => {
            if (!changeOrder.priceData) return acc;
            return acc + changeOrder.priceData.priceData.totalPrice;
          },
          0,
        );

        return priceToString(totalContractPrice + totalChangeOrderPrice);
      },
    },
    {
      title: '수금액(원)',
      width: '10%',
      align: 'right',
      render: (text: any, record: ProjectRevenue) => {
        const totalIncome = record.incomes.reduce(
          (acc: number, income: any) => {
            return acc + income.amount;
          },
          0,
        );

        return priceToString(totalIncome);
      },
    },
    {
      title: '미수금액(원)',
      width: '10%',
      align: 'right',
      render: (text: any, record: ProjectRevenue) => {
        const totalContractPrice = record.contracts.reduce(
          (acc: number, contract: any) => {
            if (!contract.priceData) return acc;
            return acc + contract.priceData.priceData.totalPrice;
          },
          0,
        );

        const totalChangeOrderPrice = record.changeOrders.reduce(
          (acc: number, changeOrder: any) => {
            if (!changeOrder.priceData) return acc;
            return acc + changeOrder.priceData.priceData.totalPrice;
          },
          0,
        );

        const totalIncome = record.incomes.reduce(
          (acc: number, income: any) => {
            return acc + income.amount;
          },
          0,
        );

        return priceToString(
          totalContractPrice + totalChangeOrderPrice - totalIncome,
        );
      },
    },
    {
      title: '상태',
      dataIndex: 'status',
      align: 'center',
      width: '7%',
      sorter: (a: ProjectRevenue, b: ProjectRevenue) =>
        projectStatusKorean[a.status].localeCompare(
          projectStatusKorean[b.status],
        ),
      render: (text: any, record: ProjectRevenue) =>
        projectStatusKorean[record.status],
    },
  ];

  return (
    <StyledDiv>
      <Row style={{ height: '20px' }} justify={'space-between'}>
        <Space>
          <Form
            form={form}
            name="advanced_search"
            className="ant-advanced-search-form"
            layout="inline"
            size="small"
            onFinish={onSearch}
          >
            <Col flex={4}>
              <Form.Item name="firstDate">
                <DatePicker
                  format={dateFormat}
                  placeholder="시작일(공사시작일 기준)"
                  style={{ width: '180px' }}
                />
              </Form.Item>
            </Col>
            <Col flex={4}>
              <Form.Item name="lastDate">
                <DatePicker
                  format={dateFormat}
                  placeholder="종료일(공사시작일 기준)"
                  style={{ width: '180px' }}
                />
              </Form.Item>
            </Col>
            <Col flex={6}>
              <Form.Item name="name">
                <Input
                  placeholder="공사명을 입력 후 엔터키를 누르세요."
                  style={{ width: '300px' }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col flex={4}>
              <Form.Item name="owner">
                <Input
                  placeholder="발주처명을 입력 후 엔터키를 누르세요."
                  style={{ width: '200px' }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col flex={2}>
              <Form.Item name="status" style={{ width: '150px' }}>
                <Select
                  allowClear
                  placeholder="상태를 선택 하세요."
                  style={{ width: '150px' }}
                >
                  <Option value="2">{projectStatusKorean['2']}</Option>
                  <Option value="0">{projectStatusKorean['0']}</Option>
                  <Option value="1">{projectStatusKorean['1']}</Option>
                  <Option value="3">{projectStatusKorean['3']}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col flex={1}>
              <Button
                htmlType="submit"
                icon={<SearchOutlined rev={undefined} />}
              >
                검색
              </Button>
            </Col>
          </Form>
        </Space>
      </Row>

      {data && (
        <Table<ProjectRevenue>
          rowKey="id"
          columns={columns}
          dataSource={data}
          style={{ marginTop: '10px' }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100', '300'],
            position: ['bottomCenter'],
          }}
          locale={{
            triggerDesc: '내림차순으로 정렬하려면 클릭하세요.',
            triggerAsc: '오름차순으로 정렬하려면 클릭하세요.',
            cancelSort: '정렬을 취소하려면 클릭하세요.',
          }}
          summary={(pageData) => {
            let totalValue1 = 0;
            let totalValue2 = 0;
            let totalValue3 = 0;

            pageData.forEach(({ contracts, changeOrders, incomes }) => {
              totalValue1 += contracts.reduce((acc: number, contract: any) => {
                if (!contract.priceData) return acc;
                return acc + contract.priceData.priceData.totalPrice;
              }, 0);

              totalValue2 += changeOrders.reduce(
                (acc: number, changeOrder: any) => {
                  if (!changeOrder.priceData) return acc;
                  return acc + changeOrder.priceData.priceData.totalPrice;
                },
                0,
              );

              totalValue3 += incomes.reduce((acc: number, income: any) => {
                return acc + income.amount;
              }, 0);
            });

            return (
              <>
                {
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {priceToString(totalValue1 + totalValue2)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {priceToString(totalValue3)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      {priceToString(totalValue1 + totalValue2 - totalValue3)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  </Table.Summary.Row>
                }
              </>
            );
          }}
        />
      )}
    </StyledDiv>
  );
}

export default RevenueListComponent;
